/* eslint-disable react/require-default-props */
import _ from 'lodash';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Select, Tooltip } from 'grab-ui';
import ProjectSuggestionItem from './ProjectSuggestionItem';
import './index.css';

const SEARCH_PROJECT_URI = '/ucd/projects/search';
const PROJECT_ES_FIELDS = {
  start: 'start',
  end: 'end',
  updatedAt: 'updatedAt',
  countryID: 'countryID',
  creator: 'creator',
  status: 'status',
  audience: 'audience',
  objective: 'objective',
  name: 'name',
  shortName: 'shortName',
  id: 'ID',
};
const INTERNAL_STATUS = {
  draft: 'DRAFT',
  active: 'ACTIVE',
  deactivated: 'DEACTIVATED',
};

export default class UcdProjectSearch extends Component {
  static propTypes = {
    pageSize: PropTypes.number, // optional prop, whose default value is 10
    value: PropTypes.string.isRequired, // optional prop, setting default project for selection, user can override it by typing
    countryID: PropTypes.string.isRequired, // optional prop, countryID  eg: 4, 6, 8
    startDate: PropTypes.string, // optional prop, should be of format YYYY-MM-DD, projects whose startDate is lesser than equal to this date will be suggested
    endDate: PropTypes.string, // optional prop, should be of format YYYY-MM-DD, projects whose endDate is greater than equal to this date will be suggested
    audience: PropTypes.string.isRequired, // optional prop, dax, pax, mex are the allowed values
    apiBaseURL: PropTypes.string.isRequired, // required prop, api gateway url having omega server path , "https://api-restricted.stg-myteksi.com/api/omega/v2"
    callback: PropTypes.func.isRequired, // required prop, callback function is called with project object when user selects a project or otherwise value is passed and its corresponding project is found
    onError: PropTypes.func.isRequired, // required prop, callback function is called when error occurred
    disabled: PropTypes.bool.isRequired, // optional prop, to disable the selection of project
    onChange: PropTypes.func, // optionoal prop, onChange function is called with projectID when user selects a project or otherwise value is passed and its corresponding project is found
    placement: PropTypes.string, // the position of the tooltip relative to the target, which can be one of top left right bottom topLeft topRight bottomLeft bottomRight leftTop leftBottom rightTop rightBottom
  };

  static defaultProps = {
    callback: () => {},
  };

  constructor(props) {
    super(props);
    this.state = {
      chosenProjectID: props.value,
      projectSuggestions: [],
      invalidProjectSuggestions: [],
      showTooltip: false,
      toolTipMessage: '',
    };
  }

  componentDidMount = () => {
    this.onSearch();
    this.getDefaultProjects();
  };

  componentDidUpdate = (preProps, preState, snapshot) => {
    ['countryID', 'startDate', 'endDate', 'audience', 'value'].forEach(prop => {
      if (preProps[prop] !== this.props[prop]) {
        this.onSearch();
      }
    });
  };

  onMouseEnterDisabledOption = () => {
    this.setState({
      showTooltip: true,
      toolTipMessage:
        "Kindly check country and user type of the project",
    });
  };

  onMouseLeaveDisabledOption = () => {
    this.setState({
      showTooltip: false,
    });
  };

  onSearch = () => {
    const { value, onError, callback, onChange } = this.props;
    if (_.isEmpty(value)) {
      this.searchProject()
        .then(res => {
          this.setProjectSuggestions(_.get(res.data, 'results', []));
        })
        .catch(err => {
          onError(`Failed to fetch project ${err}`);
        });
    } else {
      this.getProjectByID(value)
        .then(res => {
          const projects = _.get(res.data, 'results', []);
          this.setProjectSuggestions(projects);
          if (projects.length > 0) {
            this.setState({
              chosenProjectID: value,
              chosenProject: projects[0],
            });
            callback(projects[0]);
            if (_.isFunction(onChange)) {
              onChange(_.get(projects[0], 'projectID'));
            }
          }
        })
        .catch(err => {
          onError(`Failed to fetch project ${err}`);
        });
    }
  };

  onProjectChange = projectName => {
    if (projectName.length > 0 && projectName.length < 4) {
      this.setState({
        showTooltip: true,
        toolTipMessage: 'Please enter more than three characters',
      });
      return;
    }
    this.setState({ showTooltip: false });
    this.onProjectSearch(projectName);
  };

  onProjectSearch = _.debounce(projectName => {
    const { onError } = this.props;
    this.searchProject(projectName)
      .then(res => {
        const searchResults = _.get(res.data, 'results', []);
        this.setProjectSuggestions(searchResults);
        if (searchResults.length === 0) {
          this.getDefaultProjects(projectName);
        }
      })
      .catch(err => {
        onError(`Failed to fetch project ${err}`);
      });
  }, 500);

  setChosenProjectID = chosenProjectID => {
    this.setState({
      chosenProjectID,
    });
  };

  setProjectSuggestions = projectSuggestions => {
    this.setState({
      projectSuggestions,
    });
  };

  getBaseRequestObject = () => ({
    sort: {
      field: PROJECT_ES_FIELDS.updatedAt,
      asc: false,
    },
    page: 1,
    pageSize: this.props.pageSize ? this.props.pageSize : 10,
    filters: [
      // filters for fetching only draft and active projects
      {
        lhs: PROJECT_ES_FIELDS.status,
        operator: 'in',
        rhs: [INTERNAL_STATUS.draft, INTERNAL_STATUS.active],
      },
    ],
  });

  getProjectByID = projectID => {
    const { apiBaseURL } = this.props;
    const url = apiBaseURL + SEARCH_PROJECT_URI;
    const request = this.getBaseRequestObject();
    request.filters.pop();
    request.filters.push({
      lhs: PROJECT_ES_FIELDS.id,
      operator: 'eq',
      rhs: projectID,
    });
    return axios.post(url, request);
  };

  getDefaultProjects = projectName => {
    const { apiBaseURL, onError } = this.props;
    const url = apiBaseURL + SEARCH_PROJECT_URI;
    const request = this.getBaseRequestObject();
    if (!_.isEmpty(projectName)) {
      request.filters.push({
        lhs: PROJECT_ES_FIELDS.name,
        operator: 'match',
        rhs: projectName,
      });
    }
    axios
      .post(url, request)
      .then(res => {
        this.setState({
          invalidProjectSuggestions: _.get(res.data, 'results', []),
        });
      })
      .catch(err => {
        onError(`Failed to fetch default projects ${err}`);
      });
  };

  selectOnChange = projectID => {
    const { callback, onChange } = this.props;
    const chosenProject = _.find(
      this.state.projectSuggestions,
      project => project.projectID === projectID,
    );
    this.setState({
      chosenProjectID: projectID,
      chosenProject,
    });
    callback(chosenProject);
    if (_.isFunction(onChange)) {
      onChange(projectID);
    }
  };

  searchProject = projectName => {
    const { apiBaseURL, audience, countryID } = this.props;
    const url = apiBaseURL + SEARCH_PROJECT_URI;
    const request = this.getBaseRequestObject();
    if (!_.isEmpty(projectName)) {
      request.filters.push({
        lhs: PROJECT_ES_FIELDS.name,
        operator: 'match',
        rhs: projectName,
      });
    }
    if (!_.isEmpty(audience)) {
      request.filters.push({
        lhs: PROJECT_ES_FIELDS.audience,
        operator: 'eq',
        rhs: audience,
      });
    }
    if (!_.isEmpty(countryID)) {
      request.filters.push({
        lhs: PROJECT_ES_FIELDS.countryID,
        operator: 'eq',
        rhs: countryID,
      });
    }
    return axios.post(url, request);
  };

  handleSelectClose = () => {
    if (this.state.chosenProjectID && this.state.chosenProject) {
      this.setState({
        showTooltip: false,
        projectSuggestions: [this.state.chosenProject],
      });
    } else {
      this.setState({ showTooltip: false });
      const { onError } = this.props;
      this.searchProject()
        .then(res => {
          this.setProjectSuggestions(_.get(res.data, 'results', []));
        })
        .catch(err => {
          onError(`Failed to fetch project ${err}`);
        });
    }
  };

  render() {
    const { disabled } = this.props;
    const { chosenProjectID } = this.state;
    return (
      <div style={{ position: 'relative' }}>
        {this.state.showTooltip && (
          <Tooltip
            placement={this.props.placement || 'top'}
            visible
            title={this.state.toolTipMessage}
          >
            <div />
          </Tooltip>
        )}
        <Select
          allowClear
          disabled={disabled}
          showSearch
          value={chosenProjectID}
          filterOption={false}
          optionLabelProp="label"
          onChange={this.selectOnChange}
          onSearch={this.onProjectChange}
          placeholder="Select a project"
          onBlur={this.handleSelectClose}
          dropdownMatchSelectWidth
          dropdownClassName={'ucdProjectSearchDropdown'}
          style={{
            width: '100%',
          }}
          notFoundContent={
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              No Project Found
            </div>
          }
        >
          {this.state.projectSuggestions.length > 0
            ? this.state.projectSuggestions.map(projectSuggestion => (
                <Select.Option
                  label={projectSuggestion.project.name}
                  value={projectSuggestion.projectID}
                  key={projectSuggestion.projectID}
                >
                  <ProjectSuggestionItem
                    projectSuggestion={projectSuggestion}
                  />
                </Select.Option>
              ))
            : this.state.invalidProjectSuggestions.map(projectSuggestion => (
                <Select.Option
                  disabled
                  label={projectSuggestion.project.name}
                  value={projectSuggestion.projectID}
                  key={projectSuggestion.projectID}
                >
                  <ProjectSuggestionItem
                    projectSuggestion={projectSuggestion}
                    onEnterCallback={this.onMouseEnterDisabledOption}
                    onLeaveCallback={this.onMouseLeaveDisabledOption}
                  />
                </Select.Option>
              ))}
        </Select>
      </div>
    );
  }
}
